import React, { useState, useEffect } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";

const SubscriptionRequests: React.FC = () => {
  const { isAuthenticated } = usePlatformAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/platform/login", { replace: true });
      return;
    }

    fetchRequests();
  }, [isAuthenticated, navigate]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platformApiService.getPendingSubscriptionRequests();
      setRequests(response.data.requests || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load subscription requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError(null);

    try {
      await platformApiService.approveSubscriptionRequest(
        selectedRequest._id,
        adminNotes.trim() || undefined
      );
      setShowApproveModal(false);
      setAdminNotes("");
      setSelectedRequest(null);
      await fetchRequests();
      alert("Subscription request approved successfully");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to approve request");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError(null);

    try {
      await platformApiService.rejectSubscriptionRequest(
        selectedRequest._id,
        adminNotes.trim() || undefined
      );
      setShowRejectModal(false);
      setAdminNotes("");
      setSelectedRequest(null);
      await fetchRequests();
      alert("Subscription request rejected");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to reject request");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Subscription Requests</h1>
        <p className="text-gray-600 mt-1">Review and approve subscription requests from stores</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white shadow p-6 rounded">
          <p className="text-gray-600 text-center">No pending subscription requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request: any) => (
            <div
              key={request._id}
              className="bg-white shadow border border-yellow-200 p-6 rounded"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    {request.requestType === "create" ? "Create Subscription" : "Renew Subscription"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Store: {(request.storeId as any)?.name || "N/A"} ({(request.storeId as any)?._id || request.storeId})
                  </p>
                </div>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded">
                  Pending
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-sm font-medium text-gray-700">Plan</div>
                  <div className="text-sm text-gray-900">
                    {(request.planId as any)?.name || "N/A"}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Billing Cycle</div>
                  <div className="text-sm text-gray-900 capitalize">{request.billingCycle}</div>
                </div>
                {request.pluginIds && request.pluginIds.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-gray-700">Plugins</div>
                    <div className="text-sm text-gray-900">
                      {request.pluginIds.join(", ")}
                    </div>
                  </div>
                )}
                {request.reason && (
                  <div className="col-span-2">
                    <div className="text-sm font-medium text-gray-700">Reason</div>
                    <div className="text-sm text-gray-900">{request.reason}</div>
                  </div>
                )}
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-700">Requested At</div>
                  <div className="text-sm text-gray-900">
                    {new Date(request.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowApproveModal(true);
                  }}
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors font-medium"
                >
                  Approve
                </button>
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowRejectModal(true);
                  }}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
                >
                  Reject
                </button>
                <a
                  href={`/platform/stores/${(request.storeId as any)?._id || request.storeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
                >
                  View Store
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Approve Subscription Request</h2>
            <p className="text-sm text-gray-600 mb-4">
              This will create/activate the subscription for the store. Payment is assumed to be handled outside the platform.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="e.g., Payment received via bank transfer, Gifted subscription, etc."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
              >
                {processing ? "Approving..." : "Approve"}
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setAdminNotes("");
                  setSelectedRequest(null);
                }}
                disabled={processing}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Reject Subscription Request</h2>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejection (optional but recommended).
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={3}
                placeholder="Reason for rejection..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
              >
                {processing ? "Rejecting..." : "Reject"}
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setAdminNotes("");
                  setSelectedRequest(null);
                }}
                disabled={processing}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionRequests;
